import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Plus, X } from 'lucide-react';
import type { CompletionPolicy, Priority, WorkType } from '@vwt/contracts';
import { defaultCompletionPolicy } from '@vwt/domain';
import { Button, Dialog, DialogContent, DialogFooter, Field, Input, NativeSelect, toast } from '@vwt/ui';
import { newIdempotencyKey, useApiMutation, useApiQuery } from '../../lib/api.js';
import { fieldErrors, toastError } from '../../lib/errors.js';
import { currentLocale, useTranslation } from '../../lib/i18n.js';
import { useMe } from '../../lib/session.js';
import { MarkdownEditor } from '../../components/markdown.js';
import { PrincipalPicker } from '../../components/common.js';

const TYPES: WorkType[] = ['FEATURE', 'BUG', 'MAINTENANCE', 'INFRASTRUCTURE', 'RESEARCH', 'ANALYSIS', 'OPERATIONS', 'REVIEW', 'OTHER'];

/** Few-step creation; no repository, sprint or estimate required (Section 1). */
export function CreateWorkDialog({ open, onOpenChange, defaults }: { open: boolean; onOpenChange: (open: boolean) => void; defaults?: { projectId?: string; teamId?: string; requestId?: string; title?: string } }) {
  const { t } = useTranslation();
  const me = useMe();
  const navigate = useNavigate();
  const teams = useApiQuery('listTeams', { query: {} }, { enabled: open });
  const projects = useApiQuery('listProjects', { query: {} }, { enabled: open });
  const create = useApiMutation('createWorkItem', { invalidate: ['listWorkItems', 'getRequest', 'getRequestWork'] });
  const [idempotencyKey, setKey] = useState(newIdempotencyKey);
  const [title, setTitle] = useState(defaults?.title ?? '');
  const [type, setType] = useState<WorkType>(me.permissions.canCreateDevelopmentWork ? 'FEATURE' : 'OPERATIONS');
  const [responsibility, setResponsibility] = useState<string>(defaults?.teamId ?? me.teams[0]?.id ?? 'personal');
  const [projectId, setProjectId] = useState(defaults?.projectId ?? '');
  const [policy, setPolicy] = useState<CompletionPolicy | ''>('');
  const [priority, setPriority] = useState<Priority>('NORMAL');
  const [description, setDescription] = useState('');
  const [criteria, setCriteria] = useState<string[]>([]);
  const [assignee, setAssignee] = useState<{ id: string; name: string } | null>(null);
  const [labels, setLabels] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setKey(newIdempotencyKey());
      setErrors({});
    }
  }, [open]);

  const project = projects.data?.data.find((p) => p.id === projectId);
  const effectivePolicy = policy || defaultCompletionPolicy(type, project?.requiresReview ?? false);

  const submit = async () => {
    try {
      const result = await create.mutateAsync({
        idempotencyKey,
        body: {
          title: title.trim(),
          type,
          completionPolicy: effectivePolicy,
          responsibility: responsibility === 'personal' ? { kind: 'personal', personalOwnerId: me.id } : { kind: 'team', teamId: responsibility },
          originalLocale: currentLocale(),
          priority,
          ...(projectId ? { projectId } : {}),
          ...(description.trim() ? { descriptionMarkdown: description } : {}),
          ...(criteria.filter((c) => c.trim()).length ? { acceptanceCriteria: criteria.filter((c) => c.trim()).map((text) => ({ text })) } : {}),
          ...(assignee ? { assigneeId: assignee.id } : {}),
          ...(labels.trim() ? { labels: labels.split(',').map((l) => l.trim()).filter(Boolean) } : {}),
          ...(defaults?.requestId ? { requestId: defaults.requestId } : {}),
        },
      });
      toast.success(t('work.create.created', { key: result!.data.key }));
      onOpenChange(false);
      navigate(`/work?selected=${result!.data.id}`);
    } catch (error) {
      setErrors(fieldErrors(error));
      toastError(error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={t('work.create.title')} className="w-[min(680px,calc(100vw-2rem))]">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
              e.preventDefault();
              void submit();
            }
          }}
          className="flex flex-col gap-4"
        >
          <Field label={t('intake.fields.title')} htmlFor="work-title" required error={errors.title}>
            <Input id="work-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={240} autoFocus required />
          </Field>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label={t('work.create.type')} htmlFor="work-type">
              <NativeSelect id="work-type" value={type} onChange={(e) => setType(e.target.value as WorkType)}>
                {TYPES.map((v) => (
                  <option key={v} value={v}>
                    {t(`status.workType.${v}`)}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field label={t('work.create.responsibility')} htmlFor="work-resp">
              <NativeSelect id="work-resp" value={responsibility} onChange={(e) => setResponsibility(e.target.value)}>
                <option value="personal">{t('work.create.personal')}</option>
                {(teams.data?.data ?? []).map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field label={t('work.create.project')} htmlFor="work-project">
              <NativeSelect id="work-project" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
                <option value="">{t('work.create.noProject')}</option>
                {(projects.data?.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.key} · {p.name}
                  </option>
                ))}
              </NativeSelect>
            </Field>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label={t('work.create.policy')} htmlFor="work-policy" hint={t(`status.policyHelp.${effectivePolicy}`)}>
              <NativeSelect id="work-policy" value={policy} onChange={(e) => setPolicy(e.target.value as CompletionPolicy)}>
                <option value="">{t(`status.policy.${effectivePolicy}`)} ✓</option>
                {(['PRODUCTION', 'DELIVERABLE', 'REVIEWED_DELIVERABLE'] as const).map((p) => (
                  <option key={p} value={p}>
                    {t(`status.policy.${p}`)}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field label={t('intake.fields.priority')} htmlFor="work-priority">
              <NativeSelect id="work-priority" value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
                {(['URGENT', 'HIGH', 'NORMAL', 'LOW'] as const).map((p) => (
                  <option key={p} value={p}>
                    {t(`status.priority.${p}`)}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field label={t('work.create.assignee')}>
              <PrincipalPicker label={t('work.create.assignee')} value={assignee} onChange={(p) => setAssignee(p ? { id: p.id, name: p.name } : null)} />
            </Field>
          </div>
          <Field label={t('work.create.description')}>
            <MarkdownEditor value={description} onChange={setDescription} minRows={4} />
          </Field>
          <Field label={t('work.create.criteria')}>
            <ul className="flex flex-col gap-1.5">
              {criteria.map((c, i) => (
                <li key={i} className="flex gap-2">
                  <Input value={c} onChange={(e) => setCriteria((list) => list.map((x, j) => (j === i ? e.target.value : x)))} placeholder={t('work.create.criterionPlaceholder')} aria-label={`${t('work.create.criteria')} ${i + 1}`} />
                  <Button type="button" variant="ghost" size="icon" aria-label={t('common.delete')} onClick={() => setCriteria((list) => list.filter((_, j) => j !== i))}>
                    <X />
                  </Button>
                </li>
              ))}
            </ul>
            <Button type="button" variant="ghost" size="sm" className="self-start" onClick={() => setCriteria((list) => [...list, ''])}>
              <Plus /> {t('work.create.addCriterion')}
            </Button>
          </Field>
          <Field label={t('work.create.labels')} htmlFor="work-labels">
            <Input id="work-labels" value={labels} onChange={(e) => setLabels(e.target.value)} placeholder={t('work.create.labelsPlaceholder')} />
          </Field>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="primary" loading={create.isPending} disabled={!title.trim()}>
              {t('common.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
